import { Card, Text } from "@overmap-ai/blocks"
import { type FormSubmission, type Stored } from "@overmap-ai/common"
import { selectFormById, selectUserById } from "@overmap-ai/core"
import { gray } from "@radix-ui/colors"
import { Link } from "@tanstack/react-router"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { IssueTimelineItemType } from "../../enums/issues"
import { modelIconMapping } from "../../mappings/icons"
import { issueTimelineItemTypeData } from "../../mappings/issues"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { LuIconWithWrapper } from "../Icons/LuIconWithWrapper"
import { FormSubmissionIndexLabel } from "../IndexLabels/FormSubmissionIndexLabel"
import { TimelineIconRail } from "../TimelineIconRail/TimelineIconRail"
import type { IssueTimelineSize } from "./typings"

interface IssueTimelineFormSubmissionItemProps {
	formSubmission: Stored<FormSubmission>
	size: IssueTimelineSize
}

export const IssueTimelineFormSubmissionItem = memo<IssueTimelineFormSubmissionItemProps>((props) => {
	const { formSubmission, size } = props

	const createdBy = useAppSelector(selectUserById(formSubmission.created_by ?? -1))
	const form = useAppSelector(selectFormById(formSubmission.form))!

	const { icon } = issueTimelineItemTypeData[IssueTimelineItemType.FORM_SUBMISSION]

	return (
		<div className="flex w-full gap-4">
			<TimelineIconRail icon={icon} size={size} />
			<Card className="flex min-w-0 grow flex-col gap-1" size={size}>
				<Text size={size} accentColor="base">
					<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
					&nbsp;attached form
					<Text accentColor="base">&nbsp;&#8226;&nbsp;{formatCompactDate(formSubmission.submitted_at)}</Text>
				</Text>

				<Text accentColor="base" className="min-w-0 truncate" size={size}>
					<LuIconWithWrapper
						icon={modelIconMapping.form}
						color={gray.gray9}
						background
						className="align-sub"
					/>
					&nbsp;
					<Link
						className={interactiveTextCva()}
						from="/$organizationId/$projectId"
						to="/$organizationId/$projectId/forms/submissions/$formSubmissionId"
						params={{
							formSubmissionId: formSubmission.uuid,
						}}
					>
						<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} highContrast />
						&nbsp;
						<Text accentColor="base">{form.name}</Text>
					</Link>
				</Text>
			</Card>
		</div>
	)
})
IssueTimelineFormSubmissionItem.displayName = "IssueTimelineFormSubmissionItem"
